/** Room identity helpers: memorable room codes, peer ids and the URL contract. */

const ADJECTIVES = [
  "swift",
  "brave",
  "calm",
  "bright",
  "warm",
  "cool",
  "wild",
  "bold",
  "quiet",
  "happy",
  "lazy",
  "fuzzy",
  "shiny",
  "tiny",
  "giant",
  "cosmic",
  "magic",
  "mystic",
  "noble",
  "rapid",
  "snowy",
  "sunny",
  "lucky",
  "witty",
];

const NOUNS = [
  "fox",
  "owl",
  "bear",
  "wolf",
  "hawk",
  "lion",
  "panda",
  "koala",
  "tiger",
  "whale",
  "eagle",
  "raven",
  "otter",
  "bunny",
  "moose",
  "gecko",
  "falcon",
  "phoenix",
  "dragon",
  "turtle",
  "dolphin",
  "penguin",
  "badger",
  "lynx",
];

export const generateRoomCode = () => {
  const adj = ADJECTIVES[Math.floor(Math.random() * ADJECTIVES.length)];
  const noun = NOUNS[Math.floor(Math.random() * NOUNS.length)];
  // To 999 rather than 99 (#764): 57,600 codes were few enough that a few
  // hundred rooms made it likely some new one was drawn twice.
  const num = Math.floor(Math.random() * 1000);
  return `${adj}-${noun}-${num}`;
};

export const generatePeerId = () =>
  `peer_${Math.random().toString(36).slice(2, 9)}`;

/**
 * The room's address: the site root, `/room/`, and the code.
 *
 * Built from the origin rather than the current address, so a link never
 * carries whatever else happened to be in the address bar. The code is a path
 * segment now (#610) — a room has an address of its own rather than being an
 * argument to the way in — and the query form it replaced is still understood
 * everywhere a visitor can paste one.
 */
export const roomPath = (roomId: string) =>
  `/room/${encodeURIComponent(roomId)}`;

export const buildRoomURL = (roomId: string) => {
  const path = roomPath(roomId);
  if (typeof window === "undefined") return path;
  return new URL(path, window.location.origin).toString();
};

/**
 * Accepts a raw code, a full room URL or a pasted query string and returns just
 * the room code, lowercased.
 */
export const extractRoomCode = (raw: string): string => {
  const value = raw.trim();
  if (!value) return "";

  const fromQuery = (search: string) =>
    new URLSearchParams(search).get("room") || "";

  if (/^https?:\/\//i.test(value)) {
    try {
      const url = new URL(value);
      const queryCode = fromQuery(url.search);
      if (queryCode) return queryCode.toLowerCase();
      // `/room/<code>` is what this application hands out; `/rooms/<code>` and
      // anything else ending in the code is what somebody else's link may look
      // like, so the last segment is taken either way.
      const lastSegment = url.pathname.split("/").filter(Boolean).pop() || "";
      return lastSegment.toLowerCase();
    } catch {
      // fall through to the plain-code path
    }
  }

  // A pasted path with no origin in front of it: `/room/swift-fox-42`.
  if (value.startsWith("/")) {
    const lastSegment = value.split("?")[0].split("/").filter(Boolean).pop();
    if (lastSegment) return lastSegment.toLowerCase();
  }

  if (value.includes("room=")) {
    const queryCode = fromQuery(value.slice(value.indexOf("?") + 1));
    if (queryCode) return queryCode.toLowerCase();
  }

  return value.toLowerCase();
};

/** The longest a room code may be. */
export const ROOM_CODE_MAX_LENGTH = 50;

const ROOM_CODE_SHAPE = /^[a-z0-9]+(-[a-z0-9]+)*$/;

/**
 * Whether a string is a room code: lowercase Latin letters and digits in
 * hyphen-separated runs, at most `ROOM_CODE_MAX_LENGTH` long.
 *
 * The one rule every way a code arrives is held to — the entry field, an
 * address, creating, the administrator's API and the MCP tool. A room is a set
 * of realtime channels named after its code, and the transport refuses to send
 * on a topic over 255 bytes while still accepting a join on one (#693): a free
 * text code of emoji opened a room in which nothing could be sent. An ASCII
 * code of fifty characters is far inside that, and is still a code somebody
 * can read down a phone.
 *
 * Expects the code already lowercased, as `extractRoomCode` leaves it.
 */
export const isRoomCode = (code: string): boolean =>
  code.length <= ROOM_CODE_MAX_LENGTH && ROOM_CODE_SHAPE.test(code);

/**
 * A room code as a name, for the one place a room is introduced rather than
 * typed: `swift-fox-42` reads as "Swift Fox 42".
 *
 * The codes are words on purpose — `generateRoomCode` puts an adjective and a
 * noun together so one can be read down a phone — and the hyphens are for the
 * address bar, not for the visitor. Nothing here is translated: this is the
 * room's own name, the same in every language, and mangling it per locale would
 * mean two people reading the same link out loud disagree about where they are.
 *
 * Anything that is not the generated shape is returned as it came. A code
 * somebody chose, or one from a version of the generator that no longer exists,
 * is theirs to be spelled the way they spelled it.
 */
export const roomName = (code: string): string => {
  const trimmed = code.trim();
  if (!/^[a-z0-9]+(-[a-z0-9]+)+$/i.test(trimmed)) return trimmed;
  return trimmed
    .split("-")
    .map((part) =>
      /^[a-z]/i.test(part) ? part[0].toUpperCase() + part.slice(1) : part,
    )
    .join(" ");
};

/**
 * A peer id that survives a reconnect.
 *
 * Minting a fresh id on every rejoin makes the same person appear twice in the
 * room (the old presence entry lingers) and changes their deterministic colour
 * and animal, so nobody — including themselves — can tell who is who. Scoping
 * the id to the browser tab keeps identity stable across reconnects while a
 * fresh tab is still a fresh participant.
 */
const SESSION_PEER_ID_KEY = "session-peer-id";

export const getSessionPeerId = (): string => {
  try {
    const existing = sessionStorage.getItem(SESSION_PEER_ID_KEY);
    if (existing) return existing;
    const fresh = generatePeerId();
    sessionStorage.setItem(SESSION_PEER_ID_KEY, fresh);
    return fresh;
  } catch {
    // Private mode / storage disabled: fall back to a per-call id.
    return generatePeerId();
  }
};

/** Forget the tab identity (used when the user intentionally leaves). */
export const resetSessionPeerId = () => {
  try {
    sessionStorage.removeItem(SESSION_PEER_ID_KEY);
  } catch {
    /* storage unavailable */
  }
};
